import PropTypes from 'prop-types';
import * as React from 'react';
import { Col, Label, Input, Row } from 'reactstrap';
import { Button } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import ToolbarActions from './ToolbarActions';
import DayFilterPicker from './DayFilterPicker';

import './TableToolbar.scss';

class MTableToolbar extends React.Component {
  stopSubmit = e => {
    e.preventDefault();
  };

  renderSearch() {
    const localization = {
      ...MTableToolbar.defaultProps.localization,
      // eslint-disable-next-line react/destructuring-assignment
      ...this.props.localization,
    };

    const { search, searchText, onSearchChanged } = this.props;

    if (search) {
      return (
        <Col md={6} lg={4}>
          <form className="form" onSubmit={this.stopSubmit}>
            <div className="form__form-group-field">
              <Label className="sr-only" htmlFor="tableSearch">
                Search
              </Label>
              <Input
                className="search-input"
                id="tableSearch"
                name="tableSearch"
                placeholder={localization.searchTooltip}
                type="search"
                value={searchText}
                onChange={event => onSearchChanged(event.target.value)}
              />
              <Button
                aria-label="Search"
                type="button"
                className="input-group-append"
                color="secondary"
                icon={<FontAwesomeIcon icon={faSearch} />}
              />
            </div>
          </form>
        </Col>
      );
    }
    return null;
  }

  renderDefaultActions() {
    const { actions } = this.props;
    return (
      <div>
        <ToolbarActions
          actions={actions && actions.filter(a => a.isFreeAction)}
        />
      </div>
    );
  }

  renderSelectedActions() {
    const { actions, selectedRows } = this.props;
    return (
      <React.Fragment>
        <ToolbarActions
          actions={actions.filter(a => !a.isFreeAction)}
          data={selectedRows}
        />
      </React.Fragment>
    );
  }

  renderActions() {
    const { selectedRows } = this.props;
    return (
      <div>
        {selectedRows && selectedRows.length > 0
          ? this.renderSelectedActions()
          : this.renderDefaultActions()}
      </div>
    );
  }

  renderDayPicker() {
    const { filters, filtersConfig, updateFilters } = this.props;
    const dateRangeFilter = filtersConfig.some(
      conf => conf.type === 'dateRange'
    );

    return dateRangeFilter ? (
      <DayFilterPicker filters={filters} updateFilters={updateFilters} />
    ) : null;
  }

  render() {
    const localization = {
      ...MTableToolbar.defaultProps.localization,
      // eslint-disable-next-line react/destructuring-assignment
      ...this.props.localization,
    };

    const {
      children,
      hasRowSelect,
      selectedRows,
      title,
      paginated,
      filters,
      filtersConfig,
      page,
      pageSize,
      totalCount,
      updateFilters,
    } = this.props;

    const selectedRowsCount =
      selectedRows && selectedRows.length > 0
        ? localization.nRowsSelected.replace('{0}', selectedRows.length)
        : title;

    const start = pageSize * page;
    const end = Math.min(start + pageSize, totalCount);
    const pagingSummary =
      paginated && totalCount > 0
        ? localization.pagingSummary
            .replace('{0}', start + 1)
            .replace('{1}', end)
            .replace('{2}', totalCount)
        : '';

    const selectFilters = filtersConfig.filter(conf => conf.type === 'select');
    const checkboxFilters = filtersConfig.some(conf => conf.type === 'checkbox')
      ? filtersConfig.filter(conf => conf.type === 'checkbox')
      : null;
    const showFilterByText = filtersConfig.some(
      conf => conf.type === 'select' || conf.type === 'dateRange'
    );

    return (
      <Row className="table-toolbar align-items-center">
        {this.renderSearch()}
        {showFilterByText && (
          <Col xs="auto">
            <span className="toolbar-label">{localization.filterBy}</span>
            {this.renderDayPicker()}
          </Col>
        )}
        {selectFilters.map(filter => (
          <Col xs="auto" className="labeled-toggle-container" key={filter.name}>
            <select
              className="form-control"
              name={`${filter.name}FilterSelect`}
              id={`${filter.name}FilterSelect`}
              onChange={e => updateFilters({ [filter.query]: e.target.value })}
              value={filters[filter.query] || filter.defaultValue}
            >
              {filter.displayName && (
                <option value="" disabled>
                  {filter.displayName}
                </option>
              )}
              {filter.options.map(option => (
                <option value={option.value} key={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
          </Col>
        ))}
        {pagingSummary && (
          <Col xs="auto" className="mr-auto">
            <p>{pagingSummary}</p>
          </Col>
        )}
        {(checkboxFilters || hasRowSelect) && (
          <Col xs={12} className="col-lg">
            <Row className="align-items-center justify-content-end">
              {children}
              {selectedRowsCount && (
                <Col xs="auto">
                  <h5>{selectedRowsCount}</h5>
                </Col>
              )}
              {checkboxFilters &&
                checkboxFilters.map(filter => (
                  <Col xs="auto" key={filter.name}>
                    <label htmlFor={filter.name}>{filter.displayName}</label>
                    <input
                      type="checkbox"
                      name={filter.name}
                      onChange={e => {
                        updateFilters({
                          [filter.query]: e.target.checked,
                        });
                      }}
                    />
                  </Col>
                ))}
              <Col xs="auto">{this.renderActions()}</Col>
            </Row>
          </Col>
        )}
      </Row>
    );
  }
}

MTableToolbar.defaultProps = {
  actions: [],
  children: '',
  hasRowSelect: true,
  localization: {
    filterBy: 'Filter By:',
    nRowsSelected: '{0} row(s) selected',
    pagingSummary: 'Showing {0} to {1} of {2}.',
    searchTooltip: 'Search',
  },
  paginated: true,
  search: true,
  searchText: '',
  selectedRows: [],
  title: '',
  filters: {},
  filtersConfig: [],
  page: 0,
  pageSize: 0,
  totalCount: 0,
  updateFilters: () => {},
};

MTableToolbar.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        filter: PropTypes.func,
      }),
      PropTypes.func,
    ])
  ),
  children: PropTypes.node,
  hasRowSelect: PropTypes.bool,
  localization: PropTypes.shape({}),
  onSearchChanged: PropTypes.func.isRequired,
  paginated: PropTypes.bool,
  search: PropTypes.bool,
  searchText: PropTypes.string,
  selectedRows: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
  filters: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string,
  }),
  filtersConfig: PropTypes.arrayOf(PropTypes.shape({})),
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalCount: PropTypes.number,
  updateFilters: PropTypes.func,
};

export default MTableToolbar;
